"use client";

import { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import { Button } from "./Button";
import { Textarea } from "./texttarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import moment from "moment/moment";
import "moment/locale/es";

interface ReviewModalProps {
  calificacion: any;
  setCalificacion: any;
  onClick: any;
  loading: any;
  booking: any;
  onClose: () => void;
}

interface ReviewData {
  description: string;
  cleanliness: number;
  truthgulness: number;
  checkin: number;
  communication: number;
  location: number;
  price_quality: number;
}

const reviewCategories = [
  {
    key: "cleanliness",
    label: "Limpieza",
    description: "¿Qué tan limpio estaba el lugar?",
  },
  {
    key: "truthgulness",
    label: "Veracidad",
    description: "¿La descripción coincidía con la realidad?",
  },
  {
    key: "checkin",
    label: "Check-in",
    description: "¿Qué tal fue el proceso de llegada?",
  },
  {
    key: "communication",
    label: "Comunicación",
    description: "¿Cómo fue la comunicación con el anfitrión?",
  },
  {
    key: "location",
    label: "Ubicación",
    description: "¿Qué te pareció la ubicación?",
  },
  {
    key: "price_quality",
    label: "Calidad Precio",
    description: "¿El precio fue justo por lo que recibiste?",
  },
];

export default function ReviewModal({
  calificacion,
  setCalificacion,
  onClick,
  loading,
  booking,
  onClose,
}: ReviewModalProps) {
  const [disabled, setDisabled] = useState(false);

  const handleRatingChange = (category: keyof ReviewData, rating: number) => {
    console.log(category);
    
    setCalificacion({
      ...calificacion,
      [category]: rating,
    });
  };

  const getAverageRating = () => {
    const ratings = reviewCategories.map(
      (cat) => calificacion[cat.key as keyof ReviewData]
    );
    const validRatings = ratings.filter((rating) => rating > 0);
    if (validRatings.length === 0) return 0;
    return (
      validRatings.reduce((sum, rating) => sum + rating, 0) /
      validRatings.length
    );
  };

  const StarRating = ({
    rating,
    onRatingChange,
    size = "w-6 h-6",
    disabled = false,
  }: {
    rating: number;
    onRatingChange: (rating: number) => void;
    size?: string;
    disabled: boolean; 
  }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            className={`${size} transition-colors hover:scale-110 transform`}
            disabled={disabled}
          >
            <Star
              className={`w-full h-full ${
                star <= rating
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300 hover:text-yellow-300"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (booking.reviews_property?.length > 0) {
      
      setCalificacion({
        ...booking.reviews_property[0],
      });
      setDisabled(true);
    }
  }, []);

  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>
              {booking.reviews_property?.length > 0
                ? "Reseña"
                : "Escribir Reseña"}
            </span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Booking Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">
              {booking.property_name || booking.excursion_name}
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Reserva #{booking.id}</div>
              <div>
                              {`Checkin ${booking.checkin} Hrs ${moment(booking.start_date)
                                .locale("es")
                                .format("D [de] MMMM [de] YYYY")}`}{" "}
                              -{" "}
                              {`Checkout ${booking.checkout} Hrs ${moment(booking.end_date)
                                .locale("es")
                                .format("D [de] MMMM [de] YYYY")} `}
              </div>
              <div>{booking.guests} huéspedes</div>
            </div>
          </div>

          {/* Overall Rating */}
          <div className="text-center">
            <div className="text-lg font-medium mb-2">Calificación General</div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <StarRating
                rating={Math.round(getAverageRating())}
                onRatingChange={() => {}}
                size="w-8 h-8"
                disabled={disabled}
              />
              <span className="text-2xl font-bold text-gray-900">
                {getAverageRating() > 0 ? getAverageRating().toFixed(1) : "0.0"}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              Basado en las calificaciones individuales
            </div>
          </div>

          {/* Individual Ratings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Califica cada aspecto</h3>
            {reviewCategories.map((category) => (
              <div key={category.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{category.label}</div>
                    <div className="text-sm text-gray-600">
                      {category.description}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StarRating
                      rating={calificacion[category.key as keyof ReviewData]}
                      onRatingChange={(rating) =>
                        handleRatingChange(
                          category.key as keyof ReviewData,
                          rating
                        )
                      }
                      disabled={disabled}
                    />
                    <span className="text-sm font-medium w-8">
                      {calificacion[category.key as keyof ReviewData] || "0"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Describe tu experiencia
            </label>
            <Textarea
              placeholder="Comparte los detalles de tu estadía para ayudar a otros viajeros..."
              value={calificacion.description}
              onChange={(e) =>
                setCalificacion({
                  ...calificacion,
                  description: e.target.value,
                })
              }
              rows={4}
              className="resize-none"
              disabled={disabled}
            />
            <div className="text-xs text-gray-500">
              {calificacion.description.length}/500 caracteres
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button
              onClick={(e) => onClick(e, booking)}
              disabled={loading || disabled}
              name="calificar"
              className="flex-1 bg-[#8B9456] hover:bg-[#7A8249] text-white"
            >
              {loading ? "Enviando..." : "Publicar Reseña"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
